import {useEffect, useState} from 'react';
import {
  PageHeader,
  SaveCancelFooter,
  Alert,
  ConfirmationModal
} from '@/components/molecules';
import {Breadcrumb} from '@/components/atoms';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';
import {CreateTierContainer} from '@/containers/ClubPlus/CreateTier/CreateTierContainer';
import {FormProvider, useForm} from 'react-hook-form';
import {
  getCreateTierSchema,
  getDefaultValues
} from '@/pages/ClubPlusPages/CreateTier/CreateTierSchemas';
import {useAppDispatch, useAppSelector} from '@/store';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {useIntl} from 'react-intl';
import {createTier} from '@/services/ClubPlus/TierService';
import './_createTier.scss';
import {zodResolver} from '@hookform/resolvers/zod';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import enUS from '@/internationalization/i18n/en-us';
import {logger} from '@/helpers/logger';

export const CreateTier = () => {
  const {formatMessage} = useIntl();
  const countryCode = useAppSelector(selectCountryCode);
  const countryName = enUS.COUNTRIES[countryCode];

  const methods = useForm({
    defaultValues: getDefaultValues(countryCode),
    resolver: zodResolver(getCreateTierSchema(countryCode)),
    mode: 'onChange'
  });

  const [cancelModal, setCancelModal] = useState(false);
  const [draftModal, setDraftModal] = useState(false);
  const [phase, setPhase] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formIsValid, setFormIsValid] = useState({
    phase1: false,
    phase2: false
  });
  const [canSaveDraft, setCanSaveDraft] = useState(false);
  const [apiError, setApiError] = useState(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const subscription = methods.watch(() => {
      const activities = methods.getValues('activities') || [];
      const hasValidActivities =
        activities.length > 0 &&
        activities.every(
          (activity) =>
            activity.activityID &&
            activity.activityName &&
            activity.multiplier > 0
        );

      const hasAtLeastOneValidActivity =
        activities.length > 0 &&
        activities.some(
          (activity) =>
            activity.activityID &&
            activity.activityName &&
            activity.multiplier > 0
        );

      const tierName = methods.getValues('name');
      const tierId = methods.getValues('tierId');

      setFormIsValid((prev) => ({
        ...prev,
        phase1: tierName && tierId && hasValidActivities
      }));

      setCanSaveDraft(tierId && hasAtLeastOneValidActivity);
    });

    return () => subscription.unsubscribe();
  }, [methods]);

  const getFormError = () => {
    if (phase === 1) {
      return !formIsValid.phase1;
    } else if (phase === 2) {
      return !methods.formState.isValid;
    }

    return false;
  };

  const handleCancelYes = () => {
    setCancelModal(false);
    setApiError(null);
    navigate(ROUTES.CLUB_HOME);
  };

  const formatTierData = (formData) => {
    const transformedContent = {};

    Object.keys(formData.content || {}).forEach((langKey) => {
      const content = formData.content[langKey];
      const {detailsDescription, detailsTermsAndConditions, ...restContent} =
        content;
      transformedContent[langKey] = {
        ...restContent,
        descriptionDetail: detailsDescription,
        termsAndConditions: detailsTermsAndConditions
      };
    });

    return {
      tierId: formData.tierId || '',
      activities: formData.activities
        .filter(
          (activity) => activity.activityID && activity.activityID.trim() !== ''
        )
        .map((activity) => ({
          id: activity.activityID,
          multiplier: parseFloat(activity.multiplier) || 0
        })),
      content: transformedContent
    };
  };

  const handleTierAction = async (isDraft = false) => {
    try {
      setIsLoading(true);
      setApiError(null);
      const formData = methods.getValues();
      const tierData = formatTierData(formData);

      await createTier(tierData, isDraft);

      dispatch(
        setToastNotifications({
          show: true,
          message: formatMessage({
            id: isDraft
              ? 'CLUB_PLUS.TIER_CREATE_DRAFT_SUCCESS'
              : 'CLUB_PLUS.TIER_CREATE_SUCCESS_MESSAGE'
          }),
          type: 'success'
        })
      );
      navigate(ROUTES.CLUB_HOME);
    } catch (error) {
      logger.error(
        `Error ${isDraft ? 'saving tier as draft' : 'publishing tier'}:`,
        error
      );

      const errorMessage = formatMessage({
        id: 'CLUB_PLUS.TIER_CREATE_ERROR_MESSAGE'
      });

      setApiError(errorMessage);

      dispatch(
        setToastNotifications({
          show: true,
          message: errorMessage,
          type: 'error'
        })
      );
    } finally {
      setIsLoading(false);

      if (isDraft) {
        setDraftModal(false);
      }
    }
  };

  return (
    <>
      {apiError && (
        <Alert
          type='error'
          message={apiError}
          show={true}
          onCloseClick={() => setApiError(null)}
        />
      )}
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: ROUTES.HOME,
              label: ''
            },
            {
              href: ROUTES.CLUB_HOME,
              label: 'Club+'
            },
            {
              href: ROUTES.CREATE_TIER,
              label: `Create Tier${countryName ? ` ${countryName}` : ''}`
            }
          ]
        }}
        id={'ast_bradcrumb'}
      />
      <PageHeader title={'Create Tier'} />
      <FormProvider {...methods}>
        <CreateTierContainer phase={phase} setPhase={setPhase} />
        <SaveCancelFooter
          handleCancel={() => {
            setApiError(null);
            setCancelModal(true);
          }}
          nextAction={() => {
            if (phase === 3) {
              handleTierAction(false);
            } else {
              setPhase((currentPhase) => currentPhase + 1);
            }
          }}
          draftAction={() => {
            setApiError(null);
            setDraftModal(true);
          }}
          isReview={phase === 3}
          FormError={getFormError() || isLoading}
          disableDraft={!canSaveDraft}
          isLoading={isLoading}
        />
      </FormProvider>
      <ConfirmationModal
        showModal={cancelModal}
        setShowModal={setCancelModal}
        handleConfirm={handleCancelYes}
        title={formatMessage({id: 'CLUB_PLUS.TIER_CANCEL_MODAL_TITLE'})}
        text={formatMessage({id: 'CLUB_PLUS.TIER_CANCEL_MODAL_TEXT'})}
        primaryButtonText={formatMessage({id: 'GENERAL.YES'})}
        secondaryButtonText={formatMessage({id: 'GENERAL.NO'})}
        type='fluid'
        className='create-tier-modal'
      />
      <ConfirmationModal
        showModal={draftModal}
        setShowModal={setDraftModal}
        handleConfirm={() => handleTierAction(true)}
        title={formatMessage({id: 'CLUB_PLUS.TIER_CREATE_DRAFT_TITLE'})}
        text={formatMessage({id: 'CLUB_PLUS.TIER_CREATE_DRAFT_TEXT'})}
        primaryButtonText={formatMessage({id: 'GENERAL.YES'})}
        secondaryButtonText={formatMessage({id: 'GENERAL.NO'})}
        type='fluid'
        className='create-tier-modal'
      />
    </>
  );
};
